import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Package, Check, AlertTriangle, Loader2, ChevronDown, ChevronUp, ScanBarcode } from 'lucide-react';
import {
  PackageConfirmationStatusDto,
  ShipmentPackageDto,
  ConfirmPackageRequest
} from '../../../../types/packing';
import { packingService } from '../../../../services/packingService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { ScanTextInput } from '../../../common/inputs';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { usePackingHideConfirmedPackages } from '../../../../hooks/usePackingHideConfirmedPackages';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { PackageInfoModal } from './PackageInfoModal';

interface PackageConfirmationSectionProps {
  groupingId: string;
  shipmentId: number;
  onAllPackagesConfirmed?: () => void;
  /** If true, package confirmation is required before finalization */
  isRequired?: boolean;
}

/**
 * PackageConfirmationSection - Section for confirming packages in a shipment.
 * Supports scanning packages in any order (non-sequential).
 * Shows packages from both batch picks (Packages table) and standalone picks (PListOrderPackages).
 */
export const PackageConfirmationSection: React.FC<PackageConfirmationSectionProps> = ({
  groupingId,
  shipmentId,
  onAllPackagesConfirmed,
  isRequired = false
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PackageConfirmationStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [confirmedInSession, setConfirmedInSession] = useState<Set<number>>(new Set());
  // Hide-mode (packing.hideConfirmedPackages): collapsed "confirmed" section toggle
  // and ids currently animating out of the remaining grid before they move there.
  const [showConfirmed, setShowConfirmed] = useState(false);
  const [leavingIds, setLeavingIds] = useState<Set<number>>(new Set());
  // Kolli-info-dialog: kolliet operatøren har trykket på for å se detaljer.
  const [infoPackage, setInfoPackage] = useState<ShipmentPackageDto | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  // Re-entrancy guard for confirms triggered faster than the async round-trip
  // (state updates are async, so a ref is needed to block overlapping scans).
  const isConfirmingRef = useRef(false);

  // Setting-driven behaviour, read early so the focus effect can react to it:
  //  - scanning.enableGlobalBarcodeCapture → scan without focusing the field
  //  - packing.hideConfirmedPackages       → hide confirmed kolli from the list
  const globalScanEnabled = useBarcodeScannerEnabled();
  const hideConfirmed = usePackingHideConfirmedPackages();

  // Load package confirmation status
  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await packingService.getPackageConfirmationStatus(groupingId);
      setStatus(data);

      // Check if all confirmed
      if (data.allConfirmed && onAllPackagesConfirmed) {
        onAllPackagesConfirmed();
      }
    } catch (err) {
      console.error('Failed to load package confirmation status:', err);
      setError(t('warehouse.modules.packing.errors.loadPackageListFailed'));
    } finally {
      setLoading(false);
    }
  }, [groupingId, onAllPackagesConfirmed]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Focus the visible input when the section expands — but NOT when global
  // barcode capture is on. In that mode the hidden MobileBarcodeCaptureInput
  // owns focus (and suppresses the on-screen keyboard on tablets), so the
  // operator can scan without the visible field ever being focused.
  useEffect(() => {
    if (isExpanded && inputRef.current && !loading && !globalScanEnabled) {
      inputRef.current.focus();
    }
  }, [isExpanded, loading, globalScanEnabled]);

  // Handle scan input
  const handleScanInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScanValue(e.target.value);
    setScanError(null);
  };

  // Check if a package is confirmed (either from database or in this session)
  const isPackageConfirmed = (pkg: ShipmentPackageDto): boolean => {
    return pkg.isConfirmed || confirmedInSession.has(pkg.packageId);
  };

  // Core confirmation routine - shared by the manual input (Enter/button) and
  // the global barcode scanner. Accepts the raw scanned/typed value and allows
  // scanning in any order. The ref-based re-entrancy guard prevents overlapping
  // confirms when scans arrive faster than the async round-trip.
  const confirmScannedPackage = async (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (!trimmed || !status) return;
    if (isConfirmingRef.current) return; // already processing a scan

    const scannedId = parseInt(trimmed, 10);
    if (isNaN(scannedId)) {
      setScanError(t('warehouse.modules.packing.errors.invalidPackageNumber'));
      setScanValue('');
      return;
    }

    // Find the scanned package in the list (allow any order)
    const scannedPackage = status.packages.find(p => p.packageId === scannedId);
    if (!scannedPackage) {
      setScanError(t('warehouse.modules.packing.errors.packageNotInShipment', { '0': scannedId.toString() }));
      setScanValue('');
      return;
    }

    // Check if already confirmed
    if (isPackageConfirmed(scannedPackage)) {
      setScanError(t('warehouse.modules.packing.errors.packageAlreadyConfirmed', { '0': scannedId.toString() }));
      setScanValue('');
      return;
    }

    isConfirmingRef.current = true;
    setIsConfirming(true);
    setScanError(null);

    try {
      const request: ConfirmPackageRequest = {
        shipmentID: shipmentId,
        scannedPackageId: scannedId,
        expectedPackageId: scannedPackage.packageId,
        sourceType: scannedPackage.sourceType,
        pListID: scannedPackage.pListID,
        orderNo: scannedPackage.orderNo ?? undefined
      };

      const response = await packingService.confirmPackage(request);

      if (response.success) {
        // Add to session confirmed set (for batch packages that don't persist to DB)
        setConfirmedInSession(prev => new Set(prev).add(scannedId));

        // Hide-mode exit animation: keep the card briefly (fading out) in the
        // remaining grid, then drop it so it moves to the collapsed section.
        setLeavingIds(prev => new Set(prev).add(scannedId));
        setTimeout(() => {
          setLeavingIds(prev => {
            const next = new Set(prev);
            next.delete(scannedId);
            return next;
          });
        }, 320);

        // Clear input
        setScanValue('');

        // Check if all confirmed after this one
        const remainingUnconfirmed = status.packages.filter(
          p => !isPackageConfirmed(p) && p.packageId !== scannedId
        );

        if (remainingUnconfirmed.length === 0 && onAllPackagesConfirmed) {
          onAllPackagesConfirmed();
        }

        // Reload status to get updated state from server
        await loadStatus();
      } else {
        setScanError(response.message || t('warehouse.modules.packing.errors.confirmPackageFailed'));
      }
    } catch (err) {
      console.error('Failed to confirm package:', err);
      setScanError(t('warehouse.modules.packing.errors.confirmPackageFailed'));
    } finally {
      isConfirmingRef.current = false;
      setIsConfirming(false);
      // With global capture on, leave focus to the hidden capture input.
      if (!globalScanEnabled) {
        inputRef.current?.focus();
      }
    }
  };

  // Handle confirm (Enter key or button) using the current input value
  const handleConfirm = () => {
    void confirmScannedPackage(scanValue);
  };

  // Handle key down (Enter to confirm)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  // Derived counts (include in-session confirmations). Computed before the early
  // returns so the scanner hook below always runs in the same order.
  const confirmedCount = status ? status.packages.filter(p => isPackageConfirmed(p)).length : 0;
  const allConfirmed = status ? confirmedCount >= status.totalPackages : false;
  const remainingCount = status ? status.totalPackages - confirmedCount : 0;
  const progressPercent = status && status.totalPackages > 0
    ? Math.round((confirmedCount / status.totalPackages) * 100)
    : 0;

  // Physical keyboard-wedge scanner: captures document-level keystrokes when no
  // input is focused (desktop). On enterprise/tablet scanners the hidden
  // MobileBarcodeCaptureInput below is the reliable path — it owns focus so the
  // operator never has to tap the visible field. Both feed confirmScannedPackage;
  // only one element holds focus at a time, so a scan is never double-handled.
  const scanner = useSimpleBarcodeScanner({
    enabled: globalScanEnabled && isExpanded && !!status && !allConfirmed,
    onScan: (barcode) => { void confirmScannedPackage(barcode); }
  });

  // Renders a single package card. Shared by the full grid (default view), the
  // remaining grid and the collapsible confirmed section (hide-mode).
  // `leaving` applies the exit animation before the card moves out of the grid.
  const renderPackageCard = (pkg: ShipmentPackageDto, confirmed: boolean, leaving = false) => (
    <div
      key={`${pkg.sourceType}-${pkg.packageId}`}
      onClick={() => setInfoPackage(pkg)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setInfoPackage(pkg); } }}
      title={t('warehouse.modules.packing.packageInfo.openHint', {}, 'Trykk for kolli-info')}
      className={`p-3 rounded-lg border-2 text-center transition-all duration-300 cursor-pointer ${
        confirmed
          ? 'border-green-300 bg-green-50 hover:border-green-400'
          : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50'
      } ${leaving ? 'opacity-0 scale-90 pointer-events-none' : ''}`}
    >
      <div className="flex items-center justify-center gap-1">
        {confirmed && <Check className="text-green-600" size={16} />}
        <span className={`text-lg font-bold ${confirmed ? 'text-green-700' : 'text-gray-700'}`}>
          {pkg.visiblePackageNo}
        </span>
      </div>
      <div className={`text-xs ${confirmed ? 'text-green-600' : 'text-gray-400'}`}>
        ID: {pkg.packageId}
      </div>
      <span className={`text-xs ${pkg.sourceType === 'Batch' ? 'text-purple-600' : 'text-gray-500'}`}>
        {pkg.sourceType === 'Batch' ? t('warehouse.modules.packing.batch') : t('warehouse.modules.packing.orderWithNumber', { '0': pkg.orderNo?.toString() || '' })}
      </span>
      {pkg.packZoneID && (
        <div className="text-xs text-indigo-600 font-medium mt-0.5">
          {pkg.packZoneID}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="animate-spin" size={20} />
          <span>{t('warehouse.modules.packing.loadingPackageList')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg border border-red-200 p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button
            onClick={loadStatus}
            className="ml-auto text-sm underline hover:no-underline"
          >
            {t('common.buttons.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!status || status.totalPackages === 0) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <Package size={20} />
          <span>{t('warehouse.modules.packing.noPackagesToConfirm')}</span>
        </div>
      </div>
    );
  }

  // Hide-mode partitions (a just-confirmed card lingers in `remaining` while it
  // animates out, then crosses over to `confirmed`).
  const remainingList = status.packages.filter(p => !isPackageConfirmed(p) || leavingIds.has(p.packageId));
  const confirmedList = status.packages.filter(p => isPackageConfirmed(p) && !leavingIds.has(p.packageId));

  return (
    <div className={`bg-white rounded-lg border ${allConfirmed ? 'border-green-300' : 'border-orange-300'} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-4 ${allConfirmed ? 'bg-green-50' : 'bg-orange-50'} hover:bg-opacity-80 transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${allConfirmed ? 'bg-green-100' : 'bg-orange-100'}`}>
            <Package className={allConfirmed ? 'text-green-600' : 'text-orange-600'} size={24} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">
              {t('warehouse.modules.packing.packageConfirmation')} {isRequired && <span className="text-red-500">*</span>}
            </h3>
            <p className="text-sm text-gray-600">
              {allConfirmed ? (
                <span className="text-green-600 font-medium">{t('warehouse.modules.packing.allPackagesConfirmed')}</span>
              ) : (
                <span>{t('warehouse.modules.packing.packagesConfirmedStatus', { '0': confirmedCount.toString(), '1': status.totalPackages.toString() })}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress badge */}
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            allConfirmed
              ? 'bg-green-100 text-green-800'
              : 'bg-orange-100 text-orange-800'
          }`}>
            {progressPercent}%
          </span>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Hidden capture input for enterprise/tablet scanners — it keeps
              focus so the operator can scan without focusing the visible field
              (and without popping the on-screen keyboard). */}
          <MobileBarcodeCaptureInput
            enabled={globalScanEnabled && !!status && !allConfirmed}
            onScan={(barcode) => { void confirmScannedPackage(barcode); }}
          />

          {/* Remaining packages info */}
          {!allConfirmed && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-600 font-medium mb-1">{t('warehouse.modules.packing.scanInAnyOrder')}</p>
              <p className="text-2xl font-bold text-blue-800">
                {remainingCount} {remainingCount === 1 ? t('warehouse.modules.packing.packageRemaining') : t('warehouse.modules.packing.packagesRemaining')}
              </p>
            </div>
          )}

          {/* Scan input */}
          {!allConfirmed && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('warehouse.modules.packing.scanPackageBarcode')}:
              </label>
              <div className="flex gap-2">
                <ScanTextInput
                  ref={inputRef}
                  value={scanValue}
                  onChange={handleScanInput}
                  onKeyDown={handleKeyDown}
                  disabled={isConfirming}
                  className={`flex-1 px-4 py-3 text-lg border-2 rounded-lg focus:outline-none transition-colors ${
                    scanError
                      ? 'border-red-500 bg-red-50 focus:border-red-600'
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder={t('warehouse.modules.packing.scanOrEnterPackageNumber')}
                />
                <button
                  onClick={handleConfirm}
                  disabled={isConfirming || !scanValue.trim()}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    isConfirming || !scanValue.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isConfirming ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Check size={20} />
                  )}
                  {t('common.buttons.confirm')}
                </button>
              </div>
              {globalScanEnabled && (
                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                  <ScanBarcode size={14} className={scanner.isScanning ? 'text-blue-600 animate-pulse' : ''} />
                  {scanner.isScanning
                    ? t('warehouse.modules.packing.globalScanScanning')
                    : t('warehouse.modules.packing.globalScanHint')}
                </p>
              )}
              {scanError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="text-red-600 flex-shrink-0" size={20} />
                  <p className="text-sm text-red-800 font-medium">{scanError}</p>
                </div>
              )}
            </div>
          )}

          {/* Package list */}
          {hideConfirmed ? (
            <>
              {/* Gjenstående (ubekreftede) — bekreftede kort animeres ut og
                  flyttes til seksjonen under, slik at kun manglende kolli blir
                  stående til slutt. */}
              {remainingList.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Package size={16} />
                    {t('warehouse.modules.packing.remainingPackages', { '0': remainingCount.toString() })}
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {remainingList.map((pkg) =>
                      renderPackageCard(pkg, isPackageConfirmed(pkg), leavingIds.has(pkg.packageId))
                    )}
                  </div>
                </div>
              )}

              {/* Bekreftet — sammenlagt som standard, kan åpnes for inspeksjon */}
              {confirmedList.length > 0 && (
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setShowConfirmed(v => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Check size={16} className="text-green-600" />
                      {t('warehouse.modules.packing.confirmedPackages', { '0': confirmedCount.toString() })}
                    </span>
                    {showConfirmed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {showConfirmed && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3 pt-0">
                      {confirmedList.map((pkg) => renderPackageCard(pkg, true))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Package size={16} />
                {t('warehouse.modules.packing.allPackages', { '0': status.totalPackages.toString() })}
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {status.packages.map((pkg) => renderPackageCard(pkg, isPackageConfirmed(pkg)))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Kolli-info-dialog */}
      <PackageInfoModal
        isOpen={!!infoPackage}
        pkg={infoPackage}
        confirmed={infoPackage ? isPackageConfirmed(infoPackage) : false}
        onClose={() => setInfoPackage(null)}
      />
    </div>
  );
};

export default PackageConfirmationSection;
